/** Roles as defined in the BRD permission matrix. */
export const ROLES = [
  'CONTRACTOR_ADMIN',
  'HSE_OFFICER',
  'SECURITY_GUARD',
  'SYSTEM_ADMIN',
  'VIEWER',
] as const
export type Role = (typeof ROLES)[number]

export const LOCALES = ['vi', 'en'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'vi'

/** Registration dossier state machine. */
export const REGISTRATION_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'INFO_REQUESTED',
  'APPROVED',
  'REJECTED',
  'EXPIRED',
  'REVOKED',
] as const
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]

/** Allowed transitions — enforced server-side, never inferred from the client. */
export const REGISTRATION_TRANSITIONS: Record<RegistrationStatus, RegistrationStatus[]> = {
  DRAFT: ['SUBMITTED'],
  SUBMITTED: ['APPROVED', 'REJECTED', 'INFO_REQUESTED'],
  INFO_REQUESTED: ['SUBMITTED'],
  APPROVED: ['EXPIRED', 'REVOKED'],
  REJECTED: [],
  EXPIRED: [],
  REVOKED: [],
}

export const WORKER_ELIGIBILITY_STATUSES = [
  'REGISTERED',
  'PENDING_INDUCTION',
  'ELIGIBLE',
  'NOT_ELIGIBLE',
] as const
export type WorkerEligibilityStatus = (typeof WORKER_ELIGIBILITY_STATUSES)[number]

export const PERMIT_STATUSES = ['ACTIVE', 'SUSPENDED', 'EXPIRED', 'REVOKED'] as const
export type PermitStatus = (typeof PERMIT_STATUSES)[number]

export const BADGE_STATUSES = ['ACTIVE', 'SUPERSEDED', 'REVOKED'] as const
export type BadgeStatus = (typeof BADGE_STATUSES)[number]

export const CONTRACTOR_STATUSES = ['ACTIVE', 'SUSPENDED', 'BLACKLISTED'] as const
export type ContractorStatus = (typeof CONTRACTOR_STATUSES)[number]

export const DOCUMENT_STATUSES = ['PENDING', 'VERIFIED', 'REJECTED', 'EXPIRED'] as const
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number]

export const DOCUMENT_APPLIES_TO = ['REGISTRATION', 'WORKER'] as const
export type DocumentAppliesTo = (typeof DOCUMENT_APPLIES_TO)[number]

export const ASSET_CATEGORIES = ['TOOL', 'EQUIPMENT', 'MATERIAL', 'VEHICLE', 'CHEMICAL'] as const
export type AssetCategory = (typeof ASSET_CATEGORIES)[number]

export const ASSET_STATUSES = ['DECLARED', 'APPROVED', 'REJECTED'] as const
export type AssetStatus = (typeof ASSET_STATUSES)[number]

export const PPE_SCOPES = ['PER_WORKER', 'PER_CREW'] as const
export type PpeScope = (typeof PPE_SCOPES)[number]

export const INDUCTION_RESULTS = ['PASS', 'FAIL'] as const
export type InductionResult = (typeof INDUCTION_RESULTS)[number]

export const ACCESS_DIRECTIONS = ['IN', 'OUT'] as const
export type AccessDirection = (typeof ACCESS_DIRECTIONS)[number]

export const ACCESS_DECISIONS = ['ALLOWED', 'DENIED'] as const
export type AccessDecision = (typeof ACCESS_DECISIONS)[number]

export const VALIDATION_SOURCES = ['SERVER', 'LOCAL_SNAPSHOT'] as const
export type ValidationSource = (typeof VALIDATION_SOURCES)[number]

export const RECONCILIATION_FLAGS = ['OK', 'MISMATCH'] as const
export type ReconciliationFlag = (typeof RECONCILIATION_FLAGS)[number]

export const REGISTRATION_DECISION_ACTIONS = ['APPROVE', 'REQUEST_INFO', 'REJECT'] as const
export type RegistrationDecisionAction = (typeof REGISTRATION_DECISION_ACTIONS)[number]

export const BADGE_LAYOUTS = ['CARD_CR80', 'A4_SHEET'] as const
export type BadgeLayout = (typeof BADGE_LAYOUTS)[number]

export const JOB_STATUSES = ['QUEUED', 'PROCESSING', 'COMPLETED', 'FAILED'] as const
export type JobStatus = (typeof JOB_STATUSES)[number]

export const NOTIFICATION_EVENT_TYPES = [
  'REGISTRATION_SUBMITTED',
  'INFO_REQUESTED',
  'REGISTRATION_APPROVED',
  'REGISTRATION_REJECTED',
  'PERMIT_EXPIRING',
  'INDUCTION_EXPIRING',
  'DOCUMENT_EXPIRING',
  'ENTRY_DENIED',
  'RECONCILIATION_MISMATCH',
  'OVERDUE_CHECKOUT',
] as const
export type NotificationEventType = (typeof NOTIFICATION_EVENT_TYPES)[number]
